import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
  Paper,
  Chip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface UploadedFile {
  id: string;
  originalName: string;
  filename: string;
  url: string;
  size: number;
  mimetype: string;
  uploadedAt: string;
}

interface FileUploadProps {
  onFileUploaded: (file: UploadedFile) => void;
  onFileRemoved?: (fileId: string) => void;
  uploadedFiles?: UploadedFile[];
  maxFiles?: number;
  maxSize?: number; // en MB
  acceptedTypes?: string[];
  multiple?: boolean;
  compact?: boolean; // Mode compact pour réduire la taille
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileUploaded,
  onFileRemoved,
  uploadedFiles = [],
  maxFiles = 5,
  maxSize = 100,
  acceptedTypes = ['*'],
  multiple = false,
  compact = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filesWithMetadata, setFilesWithMetadata] = useState<UploadedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  // Effet pour faire disparaître automatiquement le message de succès
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000); // Disparaît après 3 secondes

      return () => clearTimeout(timer);
    }
  }, [success]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 octets';
    const k = 1024;
    const sizes = ['octets', 'Ko', 'Mo', 'Go'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Fonction pour récupérer les métadonnées d'un fichier
  const getFileMetadata = async (filename: string): Promise<Partial<UploadedFile>> => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/upload/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const fileInfo = data.data.files.find((file: any) => file.filename === filename);
        if (fileInfo) {
          return {
            size: fileInfo.size || 0,
            mimetype: fileInfo.mimetype || 'application/octet-stream',
            uploadedAt: fileInfo.uploadedAt
          };
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des métadonnées:', error);
    }
    return { size: 0, mimetype: 'application/octet-stream' };
  };

  // Charger les métadonnées des fichiers existants
  React.useEffect(() => {
    const loadFileMetadata = async () => {
      const filesWithMetadata = await Promise.all(
        uploadedFiles.map(async (file) => {
          if (file.size === 0) {
            // Récupérer les métadonnées pour les fichiers sans taille
            const metadata = await getFileMetadata(file.filename);
            return { ...file, ...metadata };
          }
          return file;
        })
      );
      setFilesWithMetadata(filesWithMetadata);
    };

    if (uploadedFiles.length > 0) {
      loadFileMetadata();
    } else {
      setFilesWithMetadata([]);
    }
  }, [uploadedFiles]);

  // Mapping des extensions vers les MIME types
  const getMimeTypeFromExtension = (extension: string): string[] => {
    const mimeMap: { [key: string]: string[] } = {
      'pdf': ['application/pdf'],
      'doc': ['application/msword'],
      'docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
      'xls': ['application/vnd.ms-excel'],
      'xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
      'ppt': ['application/vnd.ms-powerpoint'],
      'pptx': ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
      'jpg': ['image/jpeg'],
      'jpeg': ['image/jpeg'],
      'png': ['image/png'],
      'gif': ['image/gif'],
      'webp': ['image/webp'],
      'txt': ['text/plain'],
      'csv': ['text/csv'],
      'mp3': ['audio/mpeg'],
      'wav': ['audio/wav'],
      'ogg': ['audio/ogg'],
      'mp4': ['video/mp4'],
      'webm': ['video/webm'],
      'avi': ['video/avi'],
      'mov': ['video/quicktime']
    };
    return mimeMap[extension.toLowerCase()] || [];
  };

  const validateFile = (file: File): string | null => {
    // Vérifier la taille
    if (file.size > maxSize * 1024 * 1024) {
      return `Le fichier ${file.name} dépasse la taille maximale de ${maxSize}MB`;
    }

    // Vérifier le type si spécifié
    if (acceptedTypes[0] !== '*') {
      // Convertir les extensions en MIME types et vérifier
      const allowedMimeTypes = acceptedTypes.flatMap(ext => getMimeTypeFromExtension(ext));
      
      if (allowedMimeTypes.length > 0 && !allowedMimeTypes.includes(file.type)) {
        return `Le type de fichier ${file.type} n'est pas accepté. Types acceptés: ${acceptedTypes.join(', ')}`;
      }
    }

    return null;
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/upload/single`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de l\'upload');
      }

      const result = await response.json();
      onFileUploaded(result.data.file);
      
      // Si single file mode, remplacer le fichier existant, sinon ajouter
      if (!multiple && maxFiles === 1) {
        setFilesWithMetadata([result.data.file]);
        setSuccess('Fichier modifié avec succès !');
      } else {
        setFilesWithMetadata(prev => [...prev, result.data.file]);
        setSuccess('Fichier uploadé avec succès !');
      }
      
      // Réinitialiser le champ de fichier
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validation
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    uploadFile(file);
  };

  const handleRemoveFile = (fileId: string) => {
    const file = filesWithMetadata.find(f => f.id === fileId);
    const fileName = file?.originalName || 'ce fichier';
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${fileName}" ? Cette action est irréversible.`)) {
      // Supprimer de la liste locale
      setFilesWithMetadata(prev => prev.filter(file => file.id !== fileId));
      
      if (onFileRemoved) {
        onFileRemoved(fileId);
      }
    }
  };

  const getFileIcon = (mimetype?: string) => {
    if (!mimetype) return '📁'; // Gestion du cas undefined/null
    if (mimetype.startsWith('image/')) return '🖼️';
    if (mimetype.startsWith('video/')) return '🎥';
    if (mimetype.startsWith('audio/')) return '🎵';
    if (mimetype.includes('pdf')) return '📄';
    if (mimetype.includes('word')) return '📝';
    if (mimetype.includes('excel') || mimetype.includes('spreadsheet')) return '📊';
    if (mimetype.includes('powerpoint') || mimetype.includes('presentation')) return '📈';
    return '📁';
  };

  // Check if we already have the max number of files
  const canUploadMore = multiple ? filesWithMetadata.length < maxFiles : filesWithMetadata.length === 0;
  const isSingleFileMode = !multiple && maxFiles === 1;

  return (
    <Box>
      {/* Zone d'upload */}
      {compact ? (
        // Mode compact - bouton simple
        <Box sx={{ mb: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept={acceptedTypes.join(',')}
            multiple={multiple}
          />
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => !uploading && canUploadMore && fileInputRef.current?.click()}
            disabled={uploading || !canUploadMore}
            fullWidth
          >
            {uploading 
              ? `Upload en cours... ${uploadProgress}%` 
              : isSingleFileMode && filesWithMetadata.length > 0
                ? 'Modifier le fichier'
                : 'Ajouter un fichier'
            }
          </Button>
          {uploading && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
          {!canUploadMore && !isSingleFileMode && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Limite de {maxFiles} fichier(s) atteinte
            </Typography>
          )}
        </Box>
      ) : (
        // Mode normal - grande zone d'upload
        <Paper
          sx={{
            p: 3,
            border: '2px dashed #ccc',
            textAlign: 'center',
            cursor: (uploading || !canUploadMore) ? 'not-allowed' : 'pointer',
            opacity: (uploading || !canUploadMore) ? 0.7 : 1,
            '&:hover': {
              borderColor: canUploadMore ? 'primary.main' : '#ccc',
              backgroundColor: canUploadMore ? 'action.hover' : 'transparent'
            }
          }}
          onClick={() => !uploading && canUploadMore && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept={acceptedTypes.join(',')}
            multiple={multiple}
          />
          
          <UploadIcon sx={{ fontSize: 48, color: canUploadMore ? 'primary.main' : 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {uploading 
              ? 'Upload en cours...' 
              : isSingleFileMode && filesWithMetadata.length > 0
                ? 'Cliquez pour modifier le fichier'
                : 'Cliquez pour sélectionner un fichier'
            }
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Taille maximale : {maxSize}MB
          </Typography>
          {!canUploadMore && !isSingleFileMode && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Limite de {maxFiles} fichier(s) atteinte
            </Typography>
          )}
          
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                {uploadProgress}%
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Messages d'erreur et de succès */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Liste des fichiers uploadés */}
      {filesWithMetadata.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Fichiers uploadés ({filesWithMetadata.length})
          </Typography>
          {filesWithMetadata.map((file) => (
            <Paper
              key={file.id}
              sx={{
                p: 2,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <Typography sx={{ mr: 2, fontSize: '1.5rem' }}>
                  {getFileIcon(file.mimetype)}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" noWrap>
                    {file.originalName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(file.size)} • {file.mimetype}
                  </Typography>
                </Box>
                <Chip
                  icon={<SuccessIcon />}
                  label="Uploadé"
                  color="success"
                  size="small"
                  sx={{ mr: 1 }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => {
                    // Utiliser l'API upload pour récupérer le fichier avec authentification
                    const token = localStorage.getItem('admin_token');
                    const filename = file.url.split('/').pop();
                    const apiUrl = `${process.env.REACT_APP_API_URL}/upload/${filename}`;
                    
                    // Créer un lien temporaire avec l'authentification
                    const link = document.createElement('a');
                    link.href = apiUrl;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    
                    // Ajouter le token d'authentification via fetch
                    fetch(apiUrl, {
                      headers: {
                        'Authorization': `Bearer ${token}`
                      }
                    })
                    .then(response => {
                      if (response.ok) {
                        return response.blob();
                      }
                      throw new Error('Erreur lors de la récupération du fichier');
                    })
                    .then(blob => {
                      const url = window.URL.createObjectURL(blob);
                      link.href = url;
                      link.click();
                      window.URL.revokeObjectURL(url);
                    })
                    .catch(error => {
                      console.error('Erreur:', error);
                      alert('Erreur lors de l\'ouverture du fichier');
                    });
                  }}
                >
                  Voir
                </Button>
                {onFileRemoved && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveFile(file.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;
